import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Home, Loader2 } from 'lucide-react';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { FaidaSignupForm } from '../components/faida/FaidaSignupForm';
import { FaidaTracker } from '../components/faida/FaidaTracker';
import { FaidaInstantForm } from '../components/faida/FaidaInstantForm';
import { FaidaLinkForm } from '../components/faida/FaidaLinkForm';

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/faida`;
const API = `${BASE}/my`;
const errMsg = (e) => {
  const d = e.response?.data?.detail;
  return (Array.isArray(d) ? d[0]?.msg : typeof d === 'string' ? d : null) || 'Failed';
};

const Onboard = ({ reg, user, lookups, tab, setTab, t, instant, link, signup }) => {
  const initial = reg || { full_name: user?.name, email: user?.email, phone: user?.phone };
  if (!lookups) return <Loader2 className="h-6 w-6 animate-spin text-primary" />;
  const tabs = [['instant', t('New to WHI', 'Mpya WHI')], ['link', t('I already have a WHI account', 'Nina akaunti ya WHI')]];
  return (
    <div className="space-y-4">
      <div className="flex gap-2" data-testid="faida-onboard-tabs">
        {tabs.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`faida-tab-${k}`}
            className={`rounded-full px-4 py-1.5 text-sm font-medium ${tab === k ? 'bg-emerald-900 text-white' : 'border border-border text-muted-foreground'}`}>{l}</button>
        ))}
      </div>
      {tab === 'link' ? <FaidaLinkForm onSubmit={link} />
        : lookups.available ? <FaidaInstantForm lookups={lookups} initial={initial} onSubmit={instant} />
          : <FaidaSignupForm initial={initial} reason={reg?.needs_info_reason} onSubmit={signup} />}
    </div>
  );
};

export default function FaidaFund() {
  const { token, user } = useAuth();
  const { t } = useLanguage();
  const [reg, setReg] = useState(undefined);
  const [lookups, setLookups] = useState(null);
  const [tab, setTab] = useState('instant');
  const h = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(() => axios.get(API, { headers: { Authorization: `Bearer ${token}` } })
    .then((r) => setReg(r.data.registration)).catch(() => setReg(null)), [token]);
  useEffect(() => { if (token) load(); }, [token, load]);
  useEffect(() => {
    if (token) axios.get(`${BASE}/whi-form`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => setLookups(r.data)).catch(() => setLookups({ available: false }));
  }, [token]);

  const instant = async (body) => {
    try {
      const { data } = await axios.post(`${BASE}/instant`, body, h);
      setReg(data.registration);
      toast.success(data.mode === 'instant' ? t('Registered with WHI', 'Umesajiliwa WHI') : t('Sent to WHI for registration', 'Imetumwa WHI kwa usajili'));
    } catch (e) { toast.error(errMsg(e)); }
  };
  const link = async (body) => {
    try { setReg((await axios.post(`${BASE}/link-existing`, body, h)).data.registration); toast.success(t('WHI account linked', 'Akaunti ya WHI imeunganishwa')); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const signup = async (body) => {
    try { setReg((await axios.post(API, body, h)).data.registration); toast.success(t('Sent to WHI', 'Imetumwa WHI')); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const report = async (body) => {
    try { setReg((await axios.post(`${API}/payments`, body, h)).data.registration); toast.success(t('Payment reported to WHI', 'Malipo yameripotiwa WHI')); return true; }
    catch (e) { toast.error(errMsg(e)); return false; }
  };

  return (
    <Layout>
      <div className="page-enter mx-auto max-w-3xl space-y-6" data-testid="faida-page">
        <div className="rounded-2xl bg-emerald-950 p-6 text-white">
          <div className="mb-2 flex items-center gap-2 text-xs uppercase tracking-wider text-emerald-300"><Home className="h-4 w-4" />Watumishi Housing Investments</div>
          <h1 className="text-3xl font-black sm:text-4xl">Faida Fund</h1>
          <p className="mt-2 text-sm text-emerald-100/80">
            {t('CMSA-approved fund. Start from TZS 10,000, no entry or exit fees, 3-month lock-in. You pay WHI directly through GePG.',
              'Mfuko ulioidhinishwa na CMSA. Anza na TZS 10,000, bila ada ya kuingia wala kutoka, kufungiwa miezi 3. Unalipa WHI moja kwa moja kupitia GePG.')}
          </p>
        </div>
        {reg === undefined ? <Loader2 className="h-6 w-6 animate-spin text-primary" />
          : !reg || reg.status === 'needs_info'
            ? <Onboard reg={reg} user={user} lookups={lookups} tab={tab} setTab={setTab} t={t} instant={instant} link={link} signup={signup} />
            : <>
              {['whi_registered', 'pending_whi_registration'].includes(reg.status) && <FaidaLinkForm onSubmit={link} note={t('Already have your WHI Account & Control Number (e.g. by SMS)? Enter them here to pay and track.', 'Tayari una Akaunti na Control Number ya WHI (mf. kwa SMS)? Ziweke hapa ili ulipe na kufuatilia.')} />}
              <FaidaTracker r={reg} onReport={report} />
            </>}
        <p className="text-xs text-muted-foreground">{t('Educational information only — not licensed financial advice (CMSA).', 'Taarifa hizi ni za elimu tu — si ushauri wa kifedha ulioidhinishwa (CMSA).')}</p>
      </div>
    </Layout>
  );
}
